import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { createClub } from '../utils/clubs';
import { AVAILABLE_AGE_GROUPS, generateTeamId } from '../utils/ageGroups';
import SEO from '../components/SEO';
import Header from '../components/Header';
import AuthFooter from '../components/AuthFooter';
import '../styles/AuthPage.css';
import '../styles/ClubSignup.css';

function ClubSignup() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [clubName, setClubName] = useState('');
  const [selectedAgeGroups, setSelectedAgeGroups] = useState([]);
  const [teamsByAgeGroup, setTeamsByAgeGroup] = useState({});
  const [teamInputs, setTeamInputs] = useState({});
  const [markupPerMonth, setMarkupPerMonth] = useState(0);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const toggleAgeGroup = (ag) => {
    setSelectedAgeGroups((prev) => {
      if (prev.includes(ag)) {
        const next = prev.filter((a) => a !== ag);
        setTeamsByAgeGroup((t) => {
          const copy = { ...t };
          delete copy[ag];
          return copy;
        });
        return next;
      }
      return [...prev, ag];
    });
  };

  const handleAddTeam = (ag) => {
    const label = (teamInputs[ag] || '').trim();
    if (!label) return;
    const id = generateTeamId(ag, label);
    setTeamsByAgeGroup((prev) => {
      const existing = prev[ag] || [];
      if (existing.some((t) => t.id === id)) return prev;
      return { ...prev, [ag]: [...existing, { id, ageGroup: ag, label }] };
    });
    setTeamInputs((prev) => ({ ...prev, [ag]: '' }));
  };

  const handleRemoveTeam = (ag, teamId) => {
    setTeamsByAgeGroup((prev) => ({
      ...prev,
      [ag]: (prev[ag] || []).filter((t) => t.id !== teamId),
    }));
  };

  const buildTeamsArray = () => {
    const teams = [];
    for (const ag of AVAILABLE_AGE_GROUPS) {
      if (selectedAgeGroups.includes(ag)) {
        const agTeams = teamsByAgeGroup[ag] || [];
        if (agTeams.length > 0) {
          teams.push(...agTeams);
        } else {
          teams.push({ id: generateTeamId(ag, 'main'), ageGroup: ag, label: 'Main' });
        }
      }
    }
    return teams;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 1) {
      if (!clubName.trim()) return;
      setStep(2);
      return;
    }

    if (step === 2) {
      if (selectedAgeGroups.length === 0) {
        setError('Select at least one age group.');
        return;
      }
      setStep(3);
      return;
    }

    setSubmitting(true);
    try {
      const teams = buildTeamsArray();
      const { clubId } = await createClub(user.uid, {
        name: clubName.trim(),
        teams,
        markupPerMonth,
      });
      navigate(`/${user.uid}/club/${clubId}/manage`);
    } catch (err) {
      console.error('[ClubSignup] Error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-page">
        <Header />
        <div className="auth-bg-glow auth-bg-glow-1" />
        <div className="auth-bg-glow auth-bg-glow-2" />
        <div className="auth-bg-overlay" />
        <div className="auth-page-center">
          <div className="auth-card" style={{ textAlign: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.6)' }}>Loading...</p>
          </div>
        </div>
        <AuthFooter />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-page">
        <SEO title="Club Signup" description="Sign up your volleyball club for the Begin Strength program." path="/clubs/signup" />
        <Header />
        <div className="auth-bg-glow auth-bg-glow-1" />
        <div className="auth-bg-glow auth-bg-glow-2" />
        <div className="auth-bg-overlay" />

        <div className="auth-page-center">
          <div className="auth-card" style={{ textAlign: 'center' }}>
            <h2 style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '36px', margin: 0, lineHeight: 1.1 }}>
              Create Your <span style={{ color: '#FF0000' }}>Club</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: 0 }}>
              To get started, create a free account first. Already have one? Sign in below.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/signup?redirect=/clubs/signup" className="auth-submit" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Create Account
              </Link>
              <Link to="/signin?redirect=/clubs/signup" className="auth-submit" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }}>
                Sign In
              </Link>
            </div>
          </div>

          <p className="auth-tagline">
            <Link to="/clubs" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'underline', fontSize: '14px' }}>Back to Club Info</Link>
          </p>
        </div>

        <AuthFooter />
      </div>
    );
  }

  const stepTitles = {
    1: <>Create Your <span style={{ color: '#FF0000' }}>Club</span></>,
    2: <>Set Up <span style={{ color: '#FF0000' }}>Teams</span></>,
    3: <>Set <span style={{ color: '#FF0000' }}>Pricing</span></>,
  };

  const stepDescriptions = {
    1: "Enter your club or program name. You'll get a unique link to share with your athletes.",
    2: 'Select which age groups your club offers, then add team labels within each.',
    3: 'Choose how much your club charges per athlete. You can change this later.',
  };

  return (
    <div className="auth-page">
      <SEO title="Club Signup" description="Sign up your volleyball club for the Begin Strength program." path="/clubs/signup" />
      <Header />
      <div className="auth-bg-glow auth-bg-glow-1" />
      <div className="auth-bg-glow auth-bg-glow-2" />
      <div className="auth-bg-overlay" />

      <div className="auth-page-center">
        <div className="auth-card">
          <h2 style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '36px', margin: 0, textAlign: 'center', lineHeight: 1.1 }}>
            {stepTitles[step]}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: 0, textAlign: 'center' }}>
            {stepDescriptions[step]}
          </p>

          {/* Step indicator */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '4px' }}>
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                style={{
                  width: '32px',
                  height: '4px',
                  borderRadius: '2px',
                  background: s <= step ? '#FF0000' : 'rgba(255,255,255,0.15)',
                  transition: 'background 0.2s',
                }}
              />
            ))}
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {step === 1 && (
              <div className="auth-input-wrapper">
                <input
                  id="clubName"
                  type="text"
                  value={clubName}
                  onChange={(e) => setClubName(e.target.value)}
                  placeholder="e.g. Acme Volleyball Club"
                  required
                  minLength={2}
                  className="auth-input"
                />
              </div>
            )}

            {step === 2 && (
              <div className="club-teams-builder">
                <div className="club-age-group-grid">
                  {AVAILABLE_AGE_GROUPS.map((ag) => (
                    <button
                      key={ag}
                      type="button"
                      className={`club-age-group-chip ${selectedAgeGroups.includes(ag) ? 'selected' : ''}`}
                      onClick={() => toggleAgeGroup(ag)}
                    >
                      {ag}
                    </button>
                  ))}
                </div>

                {selectedAgeGroups.length > 0 && (
                  <div className="club-teams-sections">
                    {AVAILABLE_AGE_GROUPS.filter((ag) => selectedAgeGroups.includes(ag)).map((ag) => (
                      <div key={ag} className="club-team-section">
                        <h4 className="club-team-section-title">{ag} Teams</h4>
                        <div className="club-team-tags">
                          {(teamsByAgeGroup[ag] || []).map((t) => (
                            <span key={t.id} className="club-team-tag">
                              {t.label}
                              <button type="button" className="club-team-tag-remove" onClick={() => handleRemoveTeam(ag, t.id)}>&times;</button>
                            </span>
                          ))}
                        </div>
                        <div className="club-team-add-row">
                          <input
                            type="text"
                            className="club-team-input"
                            placeholder="e.g. 1's, Gold, National"
                            value={teamInputs[ag] || ''}
                            onChange={(e) => setTeamInputs((prev) => ({ ...prev, [ag]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddTeam(ag);
                              }
                            }}
                          />
                          <button type="button" className="club-team-add-btn" onClick={() => handleAddTeam(ag)}>Add</button>
                        </div>
                        {(teamsByAgeGroup[ag] || []).length === 0 && (
                          <p className="club-team-hint">No teams added yet. A default "Main" team will be created.</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div style={{ textAlign: 'center' }}>
                <div className="club-pricing-equation" style={{ justifyContent: 'center', marginTop: '8px' }}>
                  <span className="club-pricing-base">$4</span>
                  <span className="club-pricing-operator">+</span>
                  <select
                    value={markupPerMonth}
                    onChange={(e) => setMarkupPerMonth(parseInt(e.target.value, 10))}
                    className="club-pricing-select"
                  >
                    {[0, 1, 2, 3, 4].map((n) => (
                      <option key={n} value={n}>${n}</option>
                    ))}
                  </select>
                  <span className="club-pricing-operator">=</span>
                  <span className="club-pricing-total">${4 + markupPerMonth}/mo</span>
                  <span className="club-pricing-label">per athlete</span>
                </div>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '16px' }}>
                  {markupPerMonth === 0
                    ? 'Athletes pay the $4/mo base rate. Your club earns nothing extra.'
                    : `Athletes pay $${4 + markupPerMonth}/mo. Your club keeps $${markupPerMonth}/mo per athlete.`}
                </p>
              </div>
            )}

            {error && <p className="auth-error">{error}</p>}

            <div style={{ display: 'flex', gap: '12px' }}>
              {step > 1 && (
                <button type="button" className="auth-submit" style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }} onClick={() => setStep(step - 1)}>
                  Back
                </button>
              )}
              <button
                type="submit"
                className="auth-submit"
                disabled={submitting || (step === 1 && !clubName.trim()) || (step === 2 && selectedAgeGroups.length === 0)}
                style={{ flex: 1 }}
              >
                {step === 1 ? 'Next: Set Up Teams' : step === 2 ? 'Next: Set Pricing' : submitting ? 'Creating...' : 'Create Club'}
              </button>
            </div>
          </form>
        </div>

        <p className="auth-tagline">
          <Link to="/clubs" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'underline', fontSize: '14px' }}>Back to Club Info</Link>
        </p>
      </div>

      <AuthFooter />
    </div>
  );
}

export default ClubSignup;
